import React, { useState, FC, useEffect, memo, useRef, Dispatch, SetStateAction } from "react"
import Chart from "react-apexcharts"
import { FilterData, GraphForecastData, ToggleViewState } from "pages/forecast/forecastTypes"

import { getRandomColor } from "utils/GenerateColors";

import headerImage from "../../assets/images/ronlogo.png"
import footerImage from "../../assets/images/power-rysun-logo.png"
import { getFileName } from "utils/helper";
import jsPDF from "jspdf";
import autoTable from 'jspdf-autotable';
import { tooltipFormat } from "utils/formatDate";


interface GraphProps {
  graphData: GraphForecastData
  downloadCsv: boolean
  setDownloadCsv: Dispatch<SetStateAction<boolean>>;
  currentUIView: ToggleViewState
  filterData: FilterData
}

interface SeriesSyntax {

  name: string,
  color: string,
  data: number[] | undefined

}

interface TableRow {
  col1: string | undefined
  col2: string | undefined
  col3: string | undefined
}

// Define the type for columns
interface TableColumn {
  title: string;
  dataKey: keyof TableRow; // Restrict dataKey to valid keys of TableRow
}
const AnalyticsComponent: FC<GraphProps> = memo(({ graphData, setDownloadCsv, downloadCsv, currentUIView, filterData }) => {
  const [inventorySeries, setInventorySeries] = useState<SeriesSyntax[]>([])
  const [salesHistorySeries, setSalesHistorySeries] = useState<SeriesSyntax[]>([])
  const [safetyStockData, setSafetyStockData] = useState<SeriesSyntax[]>([])

  const [categoriesName, setCategoriesName] = useState<string[]>([])
  const [productLegend, setProductLegend] = useState<string[]>([])

  const chartRef = useRef<any>();

  const [date, setDate] = useState({
    pastDate: "",
    futureDate: "",
    lengthOfInventory: null
  })

  useEffect(() => {
    graphData && makePlot()

  }, [graphData?.result])
  // plot code
  const makePlot = () => {

    let InventoryArray: any = []
    let SalesArray: any = []
    let SafetyStock: any = []
    let productNames: any[] = []
    let localCategoriesName: string[] = []
    let colorIndex = 0

    graphData?.result?.forEach((obj) => {


      const { total_avg_quantity, category, total_avg_sales, product_name, product_id } = obj

      const localInventoryData = total_avg_quantity.length > 0 ? total_avg_quantity.map((item: any) => ({ x: item.date, y: item.quantity })) : []
      const localSalesData = total_avg_sales.length > 0 ? total_avg_sales.map((item: any) => ({ x: item.date, y: item.quantity })) : []
      // const localSafetyStock = safety_stock.map((item: any) => ({ x: item.date, y: item.quantity }))
      const { randomDarkColor, randomLightColor } = getRandomColor(colorIndex)


      InventoryArray.push({ name: `${category} (IF)`, color: randomDarkColor, data: localInventoryData, })
      SalesArray.push({ name: `${category} (SH)`, color: randomLightColor, data: localSalesData, })
      //  SafetyStock.push({ name: `${category} (SS)`, color: randomDarkColor, data: localSafetyStock, strokeDashArray: 10.5  })

      obj.product_id ? productNames.push(`${category} - ${product_name} (${product_id})`) : productNames = []


      localCategoriesName.push(category)

      if (colorIndex < 4) {
        colorIndex = colorIndex + 1

      }
      else {
        colorIndex = 0
      }
    })

    const maxLengthObjectSales = SalesArray.reduce((max: any, current: any) => current.data.length > max.data.length ? current : max, SalesArray[0]);

    const maxLengthObjectInventory = InventoryArray.reduce((max: any, current: any) => current.data.length > max.data.length ? current : max, InventoryArray[0])

    setDate(prev => ({
      ...prev, futureDate: maxLengthObjectInventory?.data?.[maxLengthObjectInventory?.data?.length - 1]?.x,
      lengthOfInventory: maxLengthObjectInventory?.data?.length, pastDate: maxLengthObjectSales?.data?.[0]?.x
    }))


    setInventorySeries(InventoryArray)
    setSalesHistorySeries(SalesArray)
    // setSafetyStockData(SafetyStock)

    setProductLegend(productNames)
    setCategoriesName(localCategoriesName)



  }



  const currentDate = new Date().toISOString().slice(0, 10)
  const pastDateValue = date.pastDate
  const forecastDateValue = inventorySeries ? (date.lengthOfInventory && date.lengthOfInventory > 1 ? date.futureDate : currentDate) : currentDate


  //graph code starts here

  const tooltipCategoryFunction = ({ series, seriesIndex, dataPointIndex, w }: any) => {

    const date = new Date(w.globals.labels[seriesIndex]);
    const xValue = tooltipFormat(date)

    const givenDataSet = series[seriesIndex]
    const foundItem = graphData.result.find(item => {

      const numericalInventoryValues = item.total_avg_quantity.length > 0 ? item.total_avg_quantity.map((values: any) => { if (values.quantity) return values.quantity }) : []
      const numericalHistoryValues = item.total_avg_sales.length > 0 ? item.total_avg_sales.map((values: any) => { if (values.quantity) return values.quantity }) : []
      // const numericalSafetyValues = item.safety_stock.map((values: any) => { if (values.quantity) return values.quantity })
      return JSON.stringify(givenDataSet) === JSON.stringify(numericalInventoryValues) ||
        JSON.stringify(givenDataSet) === JSON.stringify(numericalHistoryValues)
      // || JSON.stringify(givenDataSet) === JSON.stringify(numericalSafetyValues);
    });

    const unique_products_count = foundItem ? foundItem.unique_products : null;

    const seriesName = w.globals.seriesNames[seriesIndex];
    let valueLabel
    if (seriesName.includes('SH')) {
      valueLabel = 'Sales History Quantity';
    } else if (seriesName.includes('IF')) {
      valueLabel = 'Forecast Quantity';
    }
    //  else if (seriesName.includes('SS')) {
    //   valueLabel = 'Safety Stock';
    // }
    const yValue = series[seriesIndex][dataPointIndex];

    return (
      `<div class="custom-chart-tooltip">
          <div class="tooltip-header"><b>Category: </b>${seriesName.splt(" ")[0]} ${xValue}</div>
          <div class="tooltip-body">
            <p><b>Unique Products: </b> ${unique_products_count}</p>
            <p><b>${valueLabel}: </b> ${yValue.toLocaleString('en-US')} units</p>
        </div>`
    );


  }


  const tooltipProductFunction = ({ series, seriesIndex, dataPointIndex, w }: any) => {

    // const date = new Date(w.globals.seriesX[seriesIndex]);
    // const xValue = tooltipFormat(date)
    
    const givenDataSet = series[seriesIndex]
    const foundItem = graphData.result.find(item => {

      const numericalInventoryValues = item.total_avg_quantity.length > 0 ? item.total_avg_quantity.map((values: any) => { if (values.quantity) return values.quantity }) : []
      const numericalHistoryValues = item.total_avg_sales.length > 0 ? item.total_avg_sales.map((values: any) => { if (values.quantity) return values.quantity }) : []
      // const numericalSafetyValues = item.safety_stock.map((values: any) => { if (values.quantity) return values.quantity })
      return JSON.stringify(givenDataSet) === JSON.stringify(numericalInventoryValues) ||
        JSON.stringify(givenDataSet) === JSON.stringify(numericalHistoryValues)
      // || JSON.stringify(givenDataSet) === JSON.stringify(numericalSafetyValues);
    });

    const productName = foundItem ? foundItem.product_name : null;
    const productId = foundItem ? foundItem.product_id : null;

    const seriesName = w.globals.seriesNames[seriesIndex];
    let valueLabel
    
    let date
    if (seriesName.includes('SH')) {
      valueLabel = 'Sales History Quantity'
    
      date = new Date(w.globals.seriesX[0][dataPointIndex])
     
    }
    else if (seriesName.includes('IF')) {
      valueLabel = 'Forecast Quantity'
    
      date = new Date(w.globals.seriesX[1][dataPointIndex])
    }
    // else if(seriesName.includes('SS')) {
    //   valueLabel ='Safety Stock'
    // };

    const xValue = tooltipFormat(date)

    const yValue = series[seriesIndex][dataPointIndex];

    return (
      `<div class="custom-chart-tooltip">
          <div class="tooltip-header"><b>Category: </b>${seriesName.split(" ")[0]} ${xValue}</div>
          <div class="tooltip-body">
            <p><b>Product ID: </b> ${productId}</p>
            <p><b>Product Name: </b> ${productName}</p>
            <p><b>${valueLabel}: </b> ${yValue.toLocaleString('en-US')} units</p>
        </div>`
    );
  }


  const tooltipFunction = graphData?.result[0]
    ? (graphData.result[0].unique_products
      ? tooltipCategoryFunction
      : (graphData.result[0].product_id ? tooltipProductFunction : () => ''))
    : () => '';


  const legendItems = productLegend && productLegend.length > 0 ? productLegend : categoriesName;


  const chartConfiguration: any = {
    options: {
      chart: {
        id: "forecast-area",
        offsetY: 50,
        toolbar: {
         
          tools: {
            download: false,
        }

      }
      },
      annotations: {
        xaxis: [//today
          {
            x: new Date(currentDate).getTime(),
            borderColor: "#FEB019",
            label: {
              borderColor: "#fff",
              style: {
                color: "#FEB019",
                background: "#fff",
                fontSize: "14px",
                fontWeight: 600,
              },
              offsetY: -23.5,
              text: "TODAY",
              orientation: "horizontal",
            },
            strokeDashArray: 10.5, // Adjust dash size
          },

          {//past
            x: new Date(pastDateValue).getTime(),
            borderColor: "#fff",
            label: {
              borderColor: "#fff",
              style: {
                color: "#00A9A1",
                background: "#fff",
                fontSize: "14px",
                fontWeight: 600,
              },
              offsetY: -20,
              offsetX: 55,
              text: "SALES HISTORY",
              orientation: "horizontal",
            },
            strokeDashArray: 0, // Adjust dash size
          },

          forecastDateValue !== currentDate &&
          {//forecast
            x: new Date(forecastDateValue).getTime(),
            borderColor: "#fff",
            label: {
              borderColor: "#fff",
              style: {
                color: "#3375A5",
                background: "#fff",
                fontSize: "14px",
                fontWeight: 600,
              },
              offsetY: -20,
              offsetX: -40,
              text: "FORECAST",
              orientation: "horizontal",
            },
            strokeDashArray: 0, // Adjust dash size
          },


        ],
      },
      stroke: {
        curve: "straight",
        width: 1,
      },

      fill: {
        // Primary colors
        type: "gradient",
      },
      dataLabels: {
        enabled: false,
      },

      markers: {
        size: 4,
        shape: "circle",
        radius: 2,
      },
      tooltip: {
        enabled: true,
        followCursor: false,
        intersect: true,
        shared: false ,
        custom: tooltipFunction
      },
      legend: {
        fontSize: "10.5px",
        show: true,
        customLegendItems: legendItems,
        labels: {
          useSeriesColors: true
        },
        position: "bottom",
        offsetY: legendItems.length >2 ? 12: -30,
        height: 150,
        horizontalAlign: legendItems.length >5 ? (graphData.result[0].unique_products ? "center" : 'left') : 'center', 
        itemMargin: {
          horizontal: 4, // Horizontal margin between items
          vertical: 2 // Vertical margin between items
        }
      },
      grid: {
        padding: {
          right: 40,
          left: 15
        },
      },
      xaxis: {
        type: "datetime",
        labels: {
          datetimeUTC: false,
        },
        tooltip: {
          enabled: false // Ensure that the x-axis tooltip is disabled
        }
      },
      yaxis: {

        // tickAmount: 4 ,
        steps: 1,
        labels: {
          formatter: function formatter(value: number): string {


            // Hide decimal numbers

            return value % 1 === 0 ? value.toLocaleString('en-US') : '';


          },
          style: {
            colors: ['grey'],
            fontSize: "12px",
            fontWeight: 600,
          },
        },
      }
    },  //series is written this way so that legend color is customized
    series: [...salesHistorySeries, ...inventorySeries,]
    ,
  }

  const filename = `${getFileName()}.pdf`;
  const handleDownload = async () => {
    if (chartRef.current) {
      try {
        const { imgURI } = await chartRef.current.chart.dataURI();
        const pdf = new jsPDF('p', 'mm', 'a4'); // Portrait, millimeters, A4 size
        const pageWidth = 210; // A4 width in mm
        const pageHeight = 297; // A4 height in mm

        // Adjust header size and position
        const headerWidth = 50; // Reduce the width to make it smaller
        const headerHeight = (56 * headerWidth) / 141; // Calculate height proportionally


        // Add the image without resizing
        pdf.addImage(headerImage, 'PNG', (pageWidth - headerWidth) / 2, 10, headerWidth, headerHeight);


        // Main Content
        pdf.setFontSize(22);
        pdf.text('Inventory Forecast', pageWidth / 2, 50, { align: 'center' }); // Centered horizontally


        const columns: TableColumn[] = [
          { title: 'Forecast By', dataKey: 'col1' },
          { title: 'Start Date', dataKey: 'col2' },
          { title: 'End Date', dataKey: 'col3' }
        ];

        const data: TableRow[] = [
          { col1: filterData.forecast_by, col2: filterData.forecast_range_start_date, col3: filterData.forecast_range_end_date },
        ];

        // Use jsPDF.autoTable
        autoTable(pdf, {
          startY: 60, // Adjust based on where you want the table to start
          head: [columns.map(col => col.title)],
          body: data.map(row => columns.map(col => row[col.dataKey] ?? "")),
          theme: 'striped', // You can customize the table appearance
          margin: { top: 10 }

        });

        pdf.setFontSize(11);
        pdf.setFont("helvetica", "normal");
        pdf.text("Graph View for inventory forecast", 15, 95);

        // Adjust graph image size and position
        const chartWidth = 1476;
        const chartHeight = 430;

        const graphWidth = pageWidth - 20; // 10mm margin on each side


        const aspectRatio = chartWidth / chartHeight;

        let graphHeight = (graphWidth / aspectRatio);

        const maxHeight = pageHeight - 90; // Leaving space for margins
        if (graphHeight > maxHeight) {

          graphHeight = maxHeight;
        }

        pdf.addImage(imgURI, 'PNG', 10, 105, graphWidth, graphHeight); // Adjust size and position as needed

        // Adjust footer size and position
        const footerWidth = 30; // Smaller width for footer
        const footerHeight = (20 * footerWidth) / 142; // Scale height proportionally
        pdf.addImage(footerImage, 'PNG', (pageWidth - footerWidth) / 2, pageHeight - footerHeight - 10, footerWidth, footerHeight); // Centered horizontally and 10mm margin from the bottom

        // Save the PDF with a defined filename
        pdf.save(filename);

      } catch (error) {
        console.error('Failed to download chart:', error);
      }
    }
  };

  if (downloadCsv && currentUIView.chartView) {

    handleDownload()
    setDownloadCsv(false)


  }

  return (

    <div className="chart" style={{ marginTop: "20px", cursor: "default" }}>

      {(inventorySeries.length > 0 || salesHistorySeries.length > 0) ? (
        <Chart options={chartConfiguration.options} series={chartConfiguration.series} height={430} type="area" ref={chartRef} />

      ) : (
        <div className="custom-empty-message">No data available for this selection</div>
      )}
    </div>
  )
})


export default AnalyticsComponent


// <p><b>Product ID: </b> ${productId}</p>
// <p><b>Product Name: </b> ${productName}</p>

